import { useMemo, useCallback } from 'react';
import { PaymentOrder, PaymentOrderItem } from '../../types';
import { useUpdatePaymentOrder } from '../../hooks/usePaymentOrders';
import { useRequisitions, useUpdateRequisition } from '../../hooks/useRequisitions';
import { useToast } from '../../contexts/ToastContext';
import { linksFromPaymentOrder, syncRequisitionItemStatus } from '../../utils/requisitionSync';

/**
 * Status transitions for payment orders. Marking an order as paid ("purchased")
 * also flips the linked requisition items to `purchased`.
 */
export function usePaymentOrderStatus(refetchOrders: () => void) {
  const { showToast } = useToast();
  const reqQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: requisitions = [], refetch: refetchReqs } = useRequisitions(reqQO);
  const updateOrder = useUpdatePaymentOrder();
  const updateReq = useUpdateRequisition();

  const setOrderStatus = useCallback(async (order: PaymentOrder, newStatus: PaymentOrder['status']) => {
    try {
      await updateOrder.execute({ id: order.id, data: { ...order, status: newStatus } });
      if (newStatus === 'paid') {
        const links = linksFromPaymentOrder(order);
        if (links.length > 0) {
          const count = await syncRequisitionItemStatus(
            requisitions, updateReq.execute, links, 'purchased',
            { purchaseDate: new Date().toISOString().split('T')[0] }
          );
          if (count > 0) showToast(`${count} requisition item(s) marked as purchased`, 'success');
          refetchReqs();
        }
      }
      refetchOrders();
    } catch (e: any) {
      showToast(e?.message || 'Failed to update payment order status', 'error');
    }
  }, [updateOrder, updateReq, requisitions, refetchReqs, refetchOrders, showToast]);

  /** Approve / mark purchased a single item inside a payment order. The linked
   *  requisition item follows, and the order's overall status is derived from
   *  its items (all purchased => paid, all at least approved => approved). */
  const setItemStatus = useCallback(async (
    order: PaymentOrder, item: PaymentOrderItem, newStatus: 'approved' | 'purchased'
  ) => {
    try {
      const items = (order.items || []).map((i: any) =>
        String(i.requisitionItemId || i.id) === String(item.requisitionItemId || item.id)
          ? { ...i, status: newStatus } : i);
      const statuses = items.map((i: any) => i.status || 'pending');
      const overall: PaymentOrder['status'] =
        statuses.every((s: string) => s === 'purchased') ? 'paid'
        : statuses.every((s: string) => s === 'approved' || s === 'purchased') ? 'approved'
        : order.status;
      await updateOrder.execute({ id: order.id, data: { ...order, documentId: order.documentId, items, status: overall } });

      if (item.requisitionId && item.requisitionItemId) {
        const reqStatus = newStatus === 'purchased' ? 'purchased' : 'approved';
        await syncRequisitionItemStatus(
          requisitions, updateReq.execute,
          [{ requisitionId: String(item.requisitionId), itemId: String(item.requisitionItemId) }],
          reqStatus,
          newStatus === 'purchased' ? { purchaseDate: new Date().toISOString().split('T')[0] } : {}
        );
        refetchReqs();
      }
      showToast(`Item "${item.details}" marked as ${newStatus}`, 'success');
      refetchOrders();
    } catch (e: any) {
      showToast(e?.message || 'Failed to update item status', 'error');
    }
  }, [updateOrder, updateReq, requisitions, refetchReqs, refetchOrders, showToast]);

  return { setOrderStatus, setItemStatus };
}
